import { useMemo } from 'react';

import { getPhoneNumbersFromContacts } from './PhoneNumbersSourceService';
import type { PhoneNumber } from './ContactTypes';
import { useContacts } from './useContacts';
import type { Contact } from './ContactDataManagementTypes';

interface PhoneNumbersState {
    isLoading: boolean;
    hasError: boolean;
    phoneNumbers: PhoneNumber[];
}

export interface PhoneNumbersOptions {
    mobileNumbersOnly?: boolean;
    filterContacts?: (contacts: Contact[]) => Contact[];
}

const mobileNumbersFilter = (contact: PhoneNumber, mobileNumbersOnly: boolean): boolean => {
    if (!mobileNumbersOnly) {
        return true;
    }

    return !!contact.isMobileNumber;
};

export const usePhoneNumbers = (options?: PhoneNumbersOptions): [PhoneNumbersState, () => void] => {
    const { allEntities: contacts, isPending, error, refetch } = useContacts();

    const mobileNumbersOnly = !!options?.mobileNumbersOnly;
    const filterContacts = options?.filterContacts;
    const phoneNumbers = useMemo(() => {
        const filteredContacts = filterContacts?.(contacts) ?? contacts;

        return getPhoneNumbersFromContacts(filteredContacts).filter((contact) =>
            mobileNumbersFilter(contact, mobileNumbersOnly),
        );
    }, [contacts, filterContacts, mobileNumbersOnly]);

    return [
        {
            isLoading: isPending,
            hasError: !!error,
            phoneNumbers,
        },
        refetch,
    ];
};
